import { useMemo, useState } from 'react'
import {Smartphone, Laptop, Watch, Tablet, Zap, Moon, Wind, RefreshCw, SunMedium, Bot} from 'lucide-react'

type Device = {
  id: string
  name: string
  Icon: typeof Smartphone
  baseHours: number
  oledMultiplier: number
  desc: string
}

const DEVICES: Device[] = [
  { id: 'iphone', name: 'iPhone', Icon: Smartphone, baseHours: 22, oledMultiplier: 1, desc: 'iOS 17+ · OLED' },
  { id: 'android', name: 'Android', Icon: Bot, baseHours: 20, oledMultiplier: 1, desc: 'Samsung / Pixel' },
  { id: 'macbook', name: 'MacBook', Icon: Laptop, baseHours: 18, oledMultiplier: 0, desc: 'Apple Silicon' },
  { id: 'watch', name: 'Apple Watch', Icon: Watch, baseHours: 18, oledMultiplier: 1, desc: 'Series 7+ · OLED' },
  { id: 'ipad', name: 'iPad', Icon: Tablet, baseHours: 10, oledMultiplier: 0.6, desc: 'Pro / Air' },
]

type Toggle = {
  id: string
  label: string
  detail: string
  Icon: typeof Zap
  gain: number
  oledOnly?: boolean
}

const TOGGLES: Toggle[] = [
  { id: 'lpm', label: 'Low Power Mode', detail: 'System throttling, mail fetch off', Icon: Zap, gain: 0.25 },
  { id: 'dark', label: 'Saturating Dark Mode', detail: 'OLED-aware pixel dimming', Icon: Moon, gain: 0.15, oledOnly: true },
  { id: 'motion', label: 'Reduce Motion Transitions', detail: 'GPU compositor relief', Icon: Wind, gain: 0.04 },
  { id: 'bg', label: 'Limit Background Refreshes', detail: 'Apps idle when off-screen', Icon: RefreshCw, gain: 0.08 },
  { id: 'bright', label: 'Restrict Brightness Peak', detail: 'Cap nits at ~70% threshold', Icon: SunMedium, gain: 0.10 },
]

function Calculator() {
  const [device, setDevice] = useState<Device>(DEVICES[0])
  const [active, setActive] = useState<Record<string, boolean>>({})

  const { totalGainPct, extraMinutes, hours, mins, percentRing } = useMemo(() => {
    let pct = 0
    for (const t of TOGGLES) {
      if (!active[t.id]) continue
      if (t.oledOnly) {
        pct += t.gain * device.oledMultiplier
      } else {
        pct += t.gain
      }
    }
    const extraTotalMinutes = Math.round(device.baseHours * 60 * pct)
    const h = Math.floor(extraTotalMinutes / 60)
    const m = extraTotalMinutes % 60
    return {
      totalGainPct: pct,
      extraMinutes: extraTotalMinutes,
      hours: h,
      mins: m,
      percentRing: Math.min(pct, 0.62) / 0.62,
    }
  }, [active, device])

  const circumference = 2 * Math.PI * 84
  const dashOffset = circumference * (1 - percentRing)

  return (
    <section id="calculator" className="relative py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section heading */}
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.24em] text-amber-700 mb-4">§ 01 · Diagnostic Engine</div>
            <h2 className="font-display text-slate-900 text-[clamp(2rem,4.5vw,3.75rem)] leading-[1] font-medium">
              The interactive battery <br className="hidden lg:block" />
              <span className="italic font-light">extension calculator.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <p className="text-slate-600 text-[15.5px] leading-[1.7]">
              Pick your device, flip the optimization switches, and watch the projected lifespan recalculate in real time. All math runs locally — no telemetry, no servers.
            </p>
          </div>
        </div>

        {/* Workbench */}
        <div className="rounded-3xl bg-bone-2 border border-slate-900/[0.06] overflow-hidden">
          {/* Device strip */}
          <div className="px-6 lg:px-10 pt-8 pb-6 border-b border-slate-900/[0.06]">
            <div className="flex items-center justify-between mb-5">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-slate-500">Step 1 · Select Platform</div>
              <div className="hidden sm:flex items-center gap-1.5 font-mono text-[10.5px] text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span>5 profiles loaded</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {DEVICES.map((d) => {
                const isOn = device.id === d.id
                return (
                  <button
                    key={d.id}
                    onClick={() => setDevice(d)}
                    className={`group relative text-left rounded-xl px-4 py-4 transition-all duration-300 ${
                      isOn
                        ? 'bg-slate-900 border border-slate-900 slate-glow'
                        : 'bg-bone border border-slate-900/[0.08] hover:border-slate-900/25'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <d.Icon className={`w-5 h-5 ${isOn ? 'text-amber-400' : 'text-slate-700'}`} strokeWidth={1.8} />
                      <span className={`w-1.5 h-1.5 rounded-full transition ${isOn ? 'bg-amber-400' : 'bg-slate-300 group-hover:bg-slate-400'}`} />
                    </div>
                    <div className={`font-display text-[18px] font-medium leading-none ${isOn ? 'text-bone' : 'text-slate-900'}`} style={isOn ? { color: '#FAF8F5' } : undefined}>{d.name}</div>
                    <div className={`font-mono text-[10px] uppercase tracking-wider mt-2 ${isOn ? 'text-slate-400' : 'text-slate-500'}`}>{d.desc}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Body grid */}
          <div className="grid lg:grid-cols-5">
            {/* Toggles */}
            <div className="lg:col-span-3 px-6 lg:px-10 py-8 lg:border-r border-slate-900/[0.06]">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-slate-500 mb-6">Step 2 · Tune optimizations</div>
              <div className="space-y-3">
                {TOGGLES.map((t) => {
                  const on = !!active[t.id]
                  const isDisabled = t.oledOnly && device.oledMultiplier === 0
                  const effectiveGain = t.oledOnly ? t.gain * device.oledMultiplier : t.gain
                  return (
                    <div
                      key={t.id}
                      className={`relative flex items-center gap-4 rounded-2xl px-4 sm:px-5 py-4 border transition-all ${
                        on
                          ? 'bg-amber-50/70 border-amber-400/40 amber-glow'
                          : 'bg-bone border-slate-900/[0.07] hover:border-slate-900/20'
                      } ${isDisabled ? 'opacity-50' : ''}`}
                    >
                      <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${
                        on ? 'bg-amber-500/15' : 'bg-slate-900/[0.04]'
                      }`}>
                        <t.Icon className={`w-5 h-5 ${on ? 'text-amber-700' : 'text-slate-600'}`} strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-[17px] font-medium text-slate-900">{t.label}</span>
                          <span className={`font-mono text-[10.5px] px-1.5 py-0.5 rounded ${
                            on ? 'bg-amber-500/20 text-amber-900' : 'bg-slate-900/[0.05] text-slate-600'
                          }`}>+{Math.round(effectiveGain * 100)}%</span>
                          {isDisabled && <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">OLED only</span>}
                        </div>
                        <div className="text-slate-500 text-[12.5px] mt-0.5">{t.detail}</div>
                      </div>
                      <button
                        disabled={isDisabled}
                        onClick={() => setActive((a) => ({ ...a, [t.id]: !a[t.id] }))}
                        aria-pressed={on}
                        className={`relative shrink-0 w-12 h-7 rounded-full transition-all duration-300 ${
                          on ? 'bg-slate-900' : 'bg-slate-300/70'
                        } ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full transition-transform duration-300 ${
                            on ? 'translate-x-5 bg-amber-400' : 'translate-x-0 bg-bone'
                          }`}
                        />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Output */}
            <div className="lg:col-span-2 px-6 lg:px-10 py-8 bg-slate-900 text-bone">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-amber-400 mb-6" style={{ color: '#FCD34D' }}>Step 3 · Live projection</div>

              {/* Ring */}
              <div className="relative flex items-center justify-center my-4">
                <svg width="200" height="200" viewBox="0 0 200 200" className="-rotate-90">
                  <circle cx="100" cy="100" r="84" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
                  <circle
                    cx="100" cy="100" r="84"
                    stroke="url(#ember)"
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.22, 1, 0.36, 1)' }}
                  />
                  <defs>
                    <linearGradient id="ember" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FCD34D" />
                      <stop offset="100%" stopColor="#F59E0B" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-slate-400">Total gain</div>
                  <div className="font-display text-5xl font-medium mt-1" style={{ color: '#FAF8F5' }}>
                    +{Math.round(totalGainPct * 100)}<span className="text-2xl text-amber-400">%</span>
                  </div>
                  <div className="font-mono text-[10px] text-slate-400 mt-1">{device.baseHours}h baseline</div>
                </div>
              </div>

              {/* Lifespan */}
              <div className="mt-6 pt-6 border-t border-white/[0.08]">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-slate-400 mb-3">Estimated extra lifespan</div>
                <div className="font-display text-[36px] leading-none font-medium" style={{ color: '#FAF8F5' }}>
                  +{hours}<span className="text-amber-400 italic font-light">h</span> {mins}<span className="text-amber-400 italic font-light">m</span>
                </div>
                <div className="text-slate-400 text-[12.5px] mt-2 leading-relaxed">
                  Per full charge cycle on your <span className="text-bone">{device.name}</span>. {extraMinutes === 0 ? 'Toggle a setting to begin.' : 'Compounding daily.'}
                </div>
              </div>

              {/* mini ticker */}
              <div className="mt-6 ticker h-1.5 rounded-full opacity-70" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Calculator
